"use client";

import Link from "next/link";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import type { SiteNavItem } from "./site-nav";
import type { SessionUser } from "@/lib/auth0";

type MobileNavProps = {
  nav: SiteNavItem[];
  user: SessionUser | null | undefined;
  isAdmin: boolean;
};

export function MobileNav({ nav, user, isAdmin }: MobileNavProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const drawerId = useId();

  const close = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  // Escape closes the drawer.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  // Body scroll lock while open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Focus trap and initial focus.
  useEffect(() => {
    if (!open) return;
    const drawer = drawerRef.current;
    if (!drawer) return;

    const focusables = () =>
      Array.from(
        drawer.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );

    const first = focusables()[0];
    first?.focus();

    const trapEl = drawer;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === firstEl || !active || !trapEl.contains(active)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (active === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    trapEl.addEventListener("keydown", onKeyDown);
    return () => trapEl.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Restore focus to the toggle when closing.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) {
      buttonRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  return (
    <div className="md:hidden">
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={drawerId}
        aria-label={open ? "Close navigation" : "Open navigation"}
        className="flex h-10 w-10 items-center justify-center rounded text-ink-secondary hover:text-ink"
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </button>

      {open && (
        <>
          <button
            type="button"
            tabIndex={-1}
            aria-hidden
            onClick={close}
            className="fixed inset-x-0 bottom-0 top-14 z-30 bg-black/40 motion-reduce:bg-black/40"
          />
          <div
            ref={drawerRef}
            id={drawerId}
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            className="fixed inset-x-0 top-14 z-40 bg-terminal-bg text-[#F8F9FB] border-t border-[#1A1F2E] shadow-lg motion-safe:animate-[mobile-nav-fade-in_120ms_ease-out]"
          >
            <nav className="mx-auto max-w-[1100px] px-2 py-2 text-[15px]">
              <ul className="flex flex-col">
                {nav.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={close}
                      className="flex min-h-12 items-center gap-2 rounded px-4 py-3 text-[#F8F9FB] hover:bg-[#12161F] hover:text-brand-neon focus-visible:bg-[#12161F] focus-visible:text-brand-neon focus-visible:outline-none"
                    >
                      <span className="text-[#5A6678]">{"//"}</span>
                      <span>{item.label}</span>
                    </Link>
                  </li>
                ))}

                {user && (
                  <>
                    <li
                      aria-hidden
                      className="my-1 border-t border-[#1A1F2E]"
                    />
                    <li>
                      <Link
                        href="/portal"
                        onClick={close}
                        className="flex min-h-12 items-center gap-2 rounded px-4 py-3 text-[#F8F9FB] hover:bg-[#12161F] hover:text-brand-neon focus-visible:bg-[#12161F] focus-visible:text-brand-neon focus-visible:outline-none"
                      >
                        <span className="text-[#5A6678]">{"//"}</span>
                        <span>portal</span>
                      </Link>
                    </li>
                    {isAdmin && (
                      <li>
                        <Link
                          href="/admin"
                          onClick={close}
                          className="flex min-h-12 items-center gap-2 rounded px-4 py-3 text-[#F8F9FB] hover:bg-[#12161F] hover:text-brand-neon focus-visible:bg-[#12161F] focus-visible:text-brand-neon focus-visible:outline-none"
                        >
                          <span className="text-[#5A6678]">{"//"}</span>
                          <span>admin</span>
                        </Link>
                      </li>
                    )}
                    <li>
                      <a
                        href="/auth/logout"
                        onClick={close}
                        className="flex min-h-12 items-center gap-2 rounded px-4 py-3 text-[#A0AEC0] hover:bg-[#12161F] hover:text-brand-neon focus-visible:bg-[#12161F] focus-visible:text-brand-neon focus-visible:outline-none"
                      >
                        <span className="text-[#5A6678]">{"//"}</span>
                        <span>logout</span>
                      </a>
                    </li>
                  </>
                )}

                {!user && (
                  <>
                    <li
                      aria-hidden
                      className="my-1 border-t border-[#1A1F2E]"
                    />
                    <li>
                      <a
                        href="/auth/login"
                        onClick={close}
                        className="flex min-h-12 items-center gap-2 rounded px-4 py-3 text-[#F8F9FB] hover:bg-[#12161F] hover:text-brand-neon focus-visible:bg-[#12161F] focus-visible:text-brand-neon focus-visible:outline-none"
                      >
                        <span className="text-[#5A6678]">{"//"}</span>
                        <span>sign in</span>
                      </a>
                    </li>
                  </>
                )}
              </ul>
            </nav>
          </div>
        </>
      )}
    </div>
  );
}

function MenuIcon() {
  return (
    <svg
      aria-hidden
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      aria-hidden
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
